(() => {
  const root = document.documentElement;
  root.classList.add('js');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initialized = new WeakSet();

  function reveal(scope) {
    const items = scope.querySelectorAll ? scope.querySelectorAll('[data-chb-reveal]') : [];
    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('chb-is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('chb-is-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    items.forEach((el) => observer.observe(el));
  }

  function cardLight(scope) {
    const cards = scope.querySelectorAll ? scope.querySelectorAll('.product-card, .resource-card') : [];
    cards.forEach((card) => {
      if (initialized.has(card)) return;
      initialized.add(card);
      card.addEventListener('pointermove', (event) => {
        const rect = card.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        card.style.setProperty('--chb-card-x', ((((event.clientX - rect.left) / rect.width) * 100)) + '%');
        card.style.setProperty('--chb-card-y', ((((event.clientY - rect.top) / rect.height) * 100)) + '%');
      }, { passive: true });
    });
  }

  function initCraft(section) {
    if (!section || initialized.has(section)) return;
    initialized.add(section);
    const chapters = [...section.querySelectorAll('[data-chb-craft-chapter]')];
    const counter = section.querySelector('[data-chb-craft-current]');
    const video = section.querySelector('video');
    const sound = section.querySelector('[data-chb-sound]');
    const soundLabel = section.querySelector('[data-chb-sound-label]');
    const scrub = section.dataset.scrub === 'true';
    let raf = 0;

    const setChapter = (chapter) => {
      chapters.forEach((item) => item.classList.toggle('is-active', item === chapter));
      if (counter && chapter) counter.textContent = String(chapter.dataset.index || 1).padStart(2, '0');
    };

    if (chapters.length) setChapter(chapters[0]);
    if ('IntersectionObserver' in window && !reduceMotion.matches) {
      const chapterObserver = new IntersectionObserver((entries) => {
        const active = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (active) setChapter(active.target);
      }, { threshold: [0.35, 0.55, 0.75] });
      chapters.forEach((chapter) => chapterObserver.observe(chapter));
    }

    const update = () => {
      raf = 0;
      const rect = section.getBoundingClientRect();
      const travel = Math.max(1, rect.height - window.innerHeight);
      const progress = Math.min(1, Math.max(0, -rect.top / travel));
      section.style.setProperty('--chb-craft-progress', progress.toFixed(4));
      if (video && scrub && video.duration && Number.isFinite(video.duration) && !reduceMotion.matches) {
        const target = Math.min(video.duration - 0.03, progress * video.duration);
        if (Math.abs(video.currentTime - target) > 0.05) video.currentTime = target;
      }
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    if (video) {
      if (scrub && !reduceMotion.matches) video.pause();
      else video.play().catch(() => {});
      video.addEventListener('loadedmetadata', update, { once: true });
    }
    update();

    if (sound && video) {
      sound.addEventListener('click', () => {
        const nextMuted = !video.muted;
        video.muted = nextMuted;
        sound.setAttribute('aria-pressed', String(!nextMuted));
        soundLabel.textContent = nextMuted ? (sound.dataset.labelOff || 'Sound off') : (sound.dataset.labelOn || 'Sound on');
        if (!scrub && video.paused) video.play().catch(() => {});
      });
    }
  }

  function initRoom(section) {
    if (!section || initialized.has(section)) return;
    initialized.add(section);
    const stage = section.querySelector('[data-chb-room-stage]');
    if (!stage) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      if (reduceMotion.matches) {
        section.style.setProperty('--chb-room-progress', '1');
        return;
      }
      const rect = stage.getBoundingClientRect();
      const travel = Math.max(1, rect.height - window.innerHeight);
      const progress = Math.min(1, Math.max(0, -rect.top / travel));
      section.style.setProperty('--chb-room-progress', progress.toFixed(4));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    section.querySelectorAll('[data-chb-hotspot]').forEach((button) => {
      button.addEventListener('click', () => {
        const open = button.getAttribute('aria-expanded') === 'true';
        section.querySelectorAll('[data-chb-hotspot]').forEach((item) => item.setAttribute('aria-expanded', 'false'));
        button.setAttribute('aria-expanded', String(!open));
      });
    });
    update();
  }

  function init(scope = document) {
    reveal(scope);
    cardLight(scope);
    const crafts = scope.matches?.('[data-chb-craft]') ? [scope] : [...(scope.querySelectorAll?.('[data-chb-craft]') || [])];
    crafts.forEach(initCraft);
    const rooms = scope.matches?.('[data-chb-room]') ? [scope] : [...(scope.querySelectorAll?.('[data-chb-room]') || [])];
    rooms.forEach(initRoom);
  }

  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();